import { useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import type { HermesChatMsg } from "../types";
import { sendHermesMessage } from "../api";
import { utcTime, ago } from "../format";

const PASS_KEY = "hermes_pass";

interface OptimisticMsg {
  id: string;
  text: string;
}

export default function TalkToHermes({ chat }: { chat: HermesChatMsg[] }) {
  const [text, setText] = useState("");
  const [pass, setPass] = useState(() => localStorage.getItem(PASS_KEY) ?? "");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "err" | null; msg: string }>({ kind: null, msg: "" });
  const [pending, setPending] = useState<OptimisticMsg[]>([]);
  const threadRef = useRef<HTMLDivElement>(null);

  // Persist passphrase as the user types it.
  useEffect(() => {
    if (pass) localStorage.setItem(PASS_KEY, pass);
  }, [pass]);

  // Drop optimistic messages once the snapshot includes a matching user message.
  useEffect(() => {
    if (!pending.length) return;
    setPending((prev) =>
      prev.filter((p) => !chat.some((m) => m.role === "user" && m.text.trim() === p.text.trim())),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat]);

  // Keep the thread scrolled to the newest message.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat, pending]);

  const canSend = text.trim().length > 0 && pass.trim().length > 0 && !sending;

  async function onSend(e: React.FormEvent) {
    e.preventDefault();
    if (!canSend) return;
    const body = text.trim();
    setSending(true);
    setStatus({ kind: null, msg: "" });
    const res = await sendHermesMessage(body, pass.trim());
    setSending(false);
    if (res.ok) {
      setText("");
      setPending((prev) => [...prev, { id: `opt-${Date.now()}`, text: body }]);
      setStatus({ kind: "ok", msg: "Sent — Hermes is thinking… (reply appears below within ~30s)" });
    } else if (res.status === 403) {
      setStatus({ kind: "err", msg: "Wrong passphrase" });
    } else if (res.status === 400) {
      setStatus({ kind: "err", msg: "Message was empty." });
    } else {
      setStatus({ kind: "err", msg: res.error || `Send failed (status ${res.status}).` });
    }
  }

  const empty = chat.length === 0 && pending.length === 0;

  return (
    <div className="hermes-chat">
      <p className="muted hermes-chat-note">
        Hermes answers from a RAG memory base (its knowledge docs, research journal, and this
        conversation). Your ideas are remembered.
      </p>

      <div className="hermes-thread" ref={threadRef} role="log" aria-label="Conversation with Hermes" aria-live="polite">
        {empty && (
          <p className="muted">
            No messages yet. Send Hermes an idea or a question below — its reply will appear here.
          </p>
        )}
        {chat.map((m) => (
          <div key={m.id} className={`chat-msg ${m.role === "user" ? "chat-user" : "chat-hermes"}`}>
            <div className="chat-bubble">
              {m.role === "hermes" ? (
                <div className="markdown chat-md">
                  <Markdown>{m.text}</Markdown>
                </div>
              ) : (
                <span>{m.text}</span>
              )}
              {m.role === "hermes" && m.refs && m.refs.length > 0 && (
                <div className="chat-refs" aria-label="Sources Hermes used">
                  {m.refs.map((r, i) => (
                    <span key={`${m.id}-ref-${i}`} className="chat-ref-chip" title={`relevance ${r.score.toFixed(2)}`}>
                      <span className="chat-ref-icon" aria-hidden="true">◆</span>
                      {r.source}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <time className="chat-time muted small" dateTime={m.ts} title={m.ts}>
              {m.role === "user" ? "You" : "Hermes"} · {utcTime(m.ts)} · {ago(m.ts)}
            </time>
          </div>
        ))}
        {pending.map((p) => (
          <div key={p.id} className="chat-msg chat-user">
            <div className="chat-bubble chat-pending">
              <span>{p.text}</span>
            </div>
            <time className="chat-time muted small">You · sending…</time>
          </div>
        ))}
      </div>

      <form className="hermes-composer" onSubmit={onSend}>
        <label className="visually-hidden" htmlFor="hermes-text">Message to Hermes</label>
        <textarea
          id="hermes-text"
          className="composer-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Share an idea or ask Hermes a question…"
          rows={3}
          disabled={sending}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onSend(e);
          }}
        />
        <div className="composer-row">
          <label className="visually-hidden" htmlFor="hermes-pass">Passphrase</label>
          <input
            id="hermes-pass"
            className="composer-pass"
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            placeholder="Passphrase"
            autoComplete="current-password"
            aria-label="Passphrase"
          />
          <button type="submit" className="composer-send" disabled={!canSend}>
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
        {status.kind && (
          <p
            className={status.kind === "err" ? "composer-status err" : "composer-status ok"}
            role="status"
            aria-live="polite"
          >
            {status.msg}
          </p>
        )}
      </form>
    </div>
  );
}
